/*
  IBO custom (see theme/CUSTOMIZATIONS.md, "Even product-card images", 2026-09-26).
  Product-card image boxes are one fixed square with a pure white (#FFFFFF) background
  (Theme settings > Product cards: Square + Natural fit; ibo.css "Even product-card
  images"). Photos inside that square came in very different crops: re-cut photos have an
  8 % white margin, older ones either wide white margins (pack looks tiny) or no margin at
  all (pack touches the box edge and looks cut). This script measures each photo once
  (96 px canvas of the already-loaded, same-origin /cdn/shop image, no extra download) and:
  - white background (every corner >= 238): scales and centres the photo so the product's
    longer side fills the same share of the square (Theme settings > IBO: Product card
    images, default 82 %). Tight photos are made smaller (down to the "smallest size"
    setting), photos with wide margins larger (up to the max zoom), and the whole product
    always stays inside the box;
  - any other background (grey studio shot, lifestyle photo): left as is, centred by
    object-fit: contain until product-media replaces the photo.
  Homepage aisle circles (collection-list, circle crop): the same measurement, fitted so the
  product's diagonal is 90 % of the circle and its longer side at most 76 %, so every circle
  shows one upright pack with the same padding and nothing cut by the round edge.
  Only CSS transforms are used (no layout change). Results are cached in localStorage.
  Settings come from data-* attributes on the script tag.
  2026-10-22 task 495: starts after the window load event, in idle time, and only for images near the viewport.
*/
(() => {
  if (window.iboCardImages) return;
  const script = document.currentScript;
  const ds = (script && script.dataset) || {};
  const TARGET = Math.min(0.96, Math.max(0.5, parseFloat(ds.fill || '0.82')));
  const MAXZ = Math.max(1, parseFloat(ds.maxZoom || '1.5'));
  const MINZ = Math.min(1, Math.max(0.5, parseFloat(ds.minZoom || '0.7')));
  const CIRCLE_DIAG = 0.9; // aisle circles: product diagonal as share of the circle
  const CIRCLE_SIDE = 0.76; // aisle circles: product's longer side at most this share
  // Not the product page's sticky add-to-cart thumbnail (.card--sticky-atc): its box does not clip (QA 2026-09-26).
  const CARD_SEL = '.card__media img.card__main-image, .card--related:not(.card--sticky-atc) .card__media img';
  const CIRCLE_SEL = '.cc-collection-list .card__media--circle img';
  const SEL = CARD_SEL + ', ' + CIRCLE_SEL;
  const KEY = 'ibo-ci-v3';
  const MAX_ENTRIES = 1500;
  const PX = 96;
  try { localStorage.removeItem('ibo-ci-v2'); } catch (e) { /* ignore */ }

  let cache = {};
  try { cache = JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch (e) { cache = {}; }
  let saveTimer = null;
  const save = () => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try {
        const keys = Object.keys(cache);
        if (keys.length > MAX_ENTRIES) keys.slice(0, keys.length - MAX_ENTRIES).forEach((k) => delete cache[k]);
        localStorage.setItem(KEY, JSON.stringify(cache));
      } catch (e) { /* storage full or blocked: work without cache */ }
    }, 400);
  };

  // Cache key: file path + version, without the width parameter.
  const keyFor = (src) => {
    if (!src || src.startsWith('data:')) return null;
    try {
      const u = new URL(src, location.href);
      return u.pathname + '?v=' + (u.searchParams.get('v') || '');
    } catch (e) { return null; }
  };
  const srcOf = (img) => {
    const cur = img.currentSrc || img.src || '';
    if (cur && !cur.startsWith('data:')) return cur;
    return img.dataset.src || '';
  };

  // Returns { a: aspect ratio, b: [x0,y0,x1,y1] content box as fractions, t: 'w'|'u'|'n' }
  function analyse(img) {
    const w0 = img.naturalWidth, h0 = img.naturalHeight;
    if (!w0 || !h0) return null;
    const sc = PX / Math.max(w0, h0);
    const w = Math.max(1, Math.round(w0 * sc)), h = Math.max(1, Math.round(h0 * sc));
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const cx = cv.getContext('2d', { willReadFrequently: true });
    cx.fillStyle = '#fff';
    cx.fillRect(0, 0, w, h);
    let d;
    try {
      cx.drawImage(img, 0, 0, w, h);
      d = cx.getImageData(0, 0, w, h).data;
    } catch (e) { return null; } // cross-origin image: leave it alone
    const at = (x, y) => { const k = (y * w + x) * 4; return [d[k], d[k + 1], d[k + 2]]; };
    const corners = [at(0, 0), at(w - 1, 0), at(0, h - 1), at(w - 1, h - 1)];
    const dist = (p, q) => Math.abs(p[0] - q[0]) + Math.abs(p[1] - q[1]) + Math.abs(p[2] - q[2]);
    let uniform = true;
    for (let i = 0; i < 4; i++) for (let j = i + 1; j < 4; j++) if (dist(corners[i], corners[j]) > 30) uniform = false;
    const bg = [0, 1, 2].map((c) => corners.map((p) => p[c]).sort((x, y) => x - y)[1]);
    if (!uniform) return { a: w0 / h0, b: [0, 0, 1, 1], t: 'n' };
    const white = bg.every((v) => v >= 238);
    // Content = pixels clearly different from the background (24 catches pale labels and
    // clear plastic better than before); a row/column counts when it has >= 2 such pixels,
    // so single JPEG specks do not widen the box.
    const rows = new Array(h).fill(0), cols = new Array(w).fill(0);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (dist(at(x, y), bg) > 24) { rows[y]++; cols[x]++; }
      }
    }
    const first = (arr) => arr.findIndex((n) => n >= 2);
    const last = (arr) => arr.length - 1 - [...arr].reverse().findIndex((n) => n >= 2);
    let y0 = first(rows), x0 = first(cols);
    if (y0 < 0 || x0 < 0) return { a: w0 / h0, b: [0, 0, 1, 1], t: 'n' };
    let y1 = last(rows), x1 = last(cols);
    // one pixel of safety margin at the 96 px scale
    x0 = Math.max(0, x0 - 1); y0 = Math.max(0, y0 - 1); x1 = Math.min(w - 1, x1 + 1); y1 = Math.min(h - 1, y1 + 1);
    return { a: w0 / h0, b: [x0 / w, y0 / h, (x1 + 1) / w, (y1 + 1) / h], t: white ? 'w' : 'u' };
  }

  function apply(img, r) {
    const box = img.parentElement;
    if (!r || !box) return;
    const circle = img.matches(CIRCLE_SEL);
    if (r.t !== 'w' && !circle) { img.classList.add('ibo-ci-done'); return; } // only white-background card photos are scaled
    // Box geometry in units of box height; boxes are square unless measured otherwise.
    const bw = box.clientWidth, bh = box.clientHeight;
    const B = bw && bh ? bw / bh : 1;
    let dw, dh;
    if (r.a > B) { dw = B; dh = B / r.a; } else { dh = 1; dw = r.a; }
    const ox = (B - dw) / 2, oy = (1 - dh) / 2;
    // A circle photo that is not on white is fitted as a whole (its own edges are the "product").
    const bb = r.t === 'w' ? r.b : [0, 0, 1, 1];
    const cx0 = ox + bb[0] * dw, cx1 = ox + bb[2] * dw;
    const cy0 = oy + bb[1] * dh, cy1 = oy + bb[3] * dh;
    const cw = Math.max(0.01, cx1 - cx0), ch = Math.max(0.01, cy1 - cy0);
    let z;
    if (circle) {
      z = Math.min(CIRCLE_DIAG / Math.hypot(cw, ch), CIRCLE_SIDE / Math.max(cw, ch), 2);
    } else {
      z = Math.min((TARGET * B) / cw, TARGET / ch);
      z = Math.max(MINZ, Math.min(z, MAXZ));
      // Keep the whole product inside the box.
      z = Math.min(z, (0.96 * B) / cw, 0.96 / ch);
    }
    const tx = -z * ((cx0 + cx1) / 2 - B / 2); // box-height units
    const ty = -z * ((cy0 + cy1) / 2 - 0.5);
    img.style.setProperty('--ibo-z', z.toFixed(3));
    img.style.setProperty('--ibo-x', ((tx / B) * 100).toFixed(2) + '%');
    img.style.setProperty('--ibo-y', (ty * 100).toFixed(2) + '%');
    img.classList.add('ibo-ci', 'ibo-ci-done');
  }

  function handle(img) {
    if (!(img instanceof HTMLImageElement) || !img.matches(SEL)) return;
    const k = keyFor(srcOf(img));
    if (!k) return;
    if (cache[k]) { apply(img, cache[k]); return; }
    const cur = img.currentSrc || img.src || '';
    if (!img.complete || !img.naturalWidth || cur.startsWith('data:')) return; // wait for the real load event
    const r = analyse(img);
    if (!r) return;
    cache[k] = r;
    save();
    apply(img, r);
  }

  // 2026-10-22 task 495 (mobile speed): no work before the window load event. After load, in idle time, an
  // IntersectionObserver hands over only images within 300 px of the viewport; the rest wait until the visitor
  // scrolls near them (all slides of a carousel together). Cards added later (filters, pagination, quick buy, cart drawer) go to the same observer.
  // Was: scan() at once, a document-wide load listener and a MutationObserver from the first script run.
  const near = new WeakSet();
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      // Carousel slides off to the side are clipped by the slider, so they never intersect until swiped in.
      // When one slide comes near, all slides of that carousel are measured, so a swipe shows no scale change.
      const car = e.target.closest('carousel-slider');
      const group = car ? car.querySelectorAll(SEL) : [e.target];
      group.forEach((img) => {
        if (near.has(img)) return;
        io.unobserve(img);
        near.add(img);
        handle(img);
      });
    }
  }, { rootMargin: '300px 0px' }) : null;
  const seen = new WeakSet();
  const watch = (img) => {
    if (seen.has(img)) return;
    seen.add(img);
    if (io) io.observe(img); else { near.add(img); handle(img); }
  };
  const scan = (root) => (root || document).querySelectorAll(SEL).forEach((img) => (near.has(img) ? handle(img) : watch(img)));

  const start = () => {
    // Real image loads (Canopy's lazy loader swaps data-src in): only images that are near the viewport.
    document.addEventListener('load', (e) => { if (near.has(e.target)) handle(e.target); }, true);
    new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (n.nodeType === 1) { if (n.matches && n.matches(SEL)) watch(n); else if (n.querySelectorAll) scan(n); }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
    // Re-centre on resize (box ratio is read at apply time).
    let rz = null;
    window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(() => scan(), 200); });
    scan();
  };
  const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 200));
  if (document.readyState === 'complete') idle(start);
  else window.addEventListener('load', () => idle(start), { once: true });
  window.iboCardImages = { scan, cache };
})();
